import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { Observable, BehaviorSubject, of, tap, catchError, map, take, shareReplay } from 'rxjs';
import { ApiService } from './api.service';
import { LocationApiResponse, LocationOption } from '../shared/types';
import {
  clearShopLocationState,
  readShopLocationState,
  readTimezoneFromLocation,
  writeShopLocationState,
} from '../shared/utils/date.util';
import { getUserDataLocationId } from '../shared/utils/auth-headers.util';
import { RoleService } from './role.service';
import { EmployeeService } from './employee.service';
import { UserRole } from '../shared/types/role.interface';

/**
 * Service that manages fetching, caching, and providing location data for the application.
 * @component
 * @selector app-location-service
 * @example
 *   <app-location-service></app-location-service>
 * @input None - This injectable service has no Angular @Input properties; dependencies are injected via constructor
 * @output Observable<LocationApiResponse[]> locations$ - Public observable that emits the current list of locations; subscribers receive updates whenever locations are loaded, refreshed, or cleared
 *
 * Purpose:
 *   - Centralizes all location-related data operations including fetching from the API, caching results, exposing reactive location data, and transforming locations for UI consumption.
 *   - Prevents duplicate API calls by sharing a cached observable and using a loading flag and time-based cache invalidation.
 *
 * Key public properties:
 *   - locations$ (Observable<LocationApiResponse[]>): Read-only observable stream of location data used by components (e.g., header, dropdowns).
 *
 * Important methods:
 *   - getLocations(): Observable<any>
 *       Fetches locations from the backend endpoint 'location/view-location-user' and returns a shared, cached observable. Uses a 5-minute cache duration and shareReplay(1). Updates locationsSubject on success and returns a safe fallback on error.
 *   - loadLocations(): void
 *       Ensures locations are loaded and updates the internal subject. Will not call the API if locations are already present.
 *   - clearLocationCache(): void
 *       Clears the cached observable, resets last fetch time, and empties the internal subject. Useful when the current user changes or on logout.
 *   - getLocationOptions(): LocationOption[]
 *       Transforms the current locations into dropdown-friendly options (label/value), filtering only active locations.
 *   - getLocationById(locationId: string): LocationApiResponse | undefined
 *       Returns a single location object from the current in-memory list by its ID.
 *   - getCurrentLocations(): LocationApiResponse[]
 *       Returns the current value of locations from the internal BehaviorSubject synchronously.
 *   - refreshLocations(): void
 *       Forces a cache invalidation and triggers a fresh API fetch, updating the subject with the result.
 *   - hasLocations(): boolean
 *       Convenience method to check if any locations are currently loaded.
 *   - getLocationIdsForCurrentUser(): Observable<string[]>
 *       Returns an observable of location ID strings appropriate for the current user role. Staff users receive a single assigned location from EmployeeService; admins (or other roles) receive all location IDs derived from getLocations().
 *   - updateLocationsFromData(locations: LocationApiResponse[]): void
 *       Syncs the service with externally-provided location data (e.g., from ProfileService) without making an API call; also clears internal cache so future getLocations() will re-fetch.
 *
 * Lifecycle / Behavior notes:
 *   - Caching: Results are cached for 5 minutes (CACHE_DURATION) using a shared observable and shareReplay(1). lastFetchTime tracks freshness.
 *   - Concurrency: isLoadingLocations prevents duplicate concurrent API calls; callers will receive the same in-flight observable.
 *   - Error handling: On API errors the service logs the error, pushes an empty array to the subject, and returns a safe fallback response to avoid breaking the UI.
 *   - Integration: Depends on ApiService for network calls, RoleService to determine current user role, and EmployeeService to fetch staff-assigned location.
 */
@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private locationsSubject = new BehaviorSubject<LocationApiResponse[]>([]);
  public locations$ = this.locationsSubject.asObservable();
  private locationsCache$?: Observable<any>;
  private lastFetchTime = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private isLoadingLocations = false; // Flag to prevent multiple simultaneous API calls
  /** Bumped on cache clear / new ensure so stale employee-profile responses are ignored. */
  private shopLocationWriteGeneration = 0;

  constructor(
    private logger: LoggerService,
    private apiService: ApiService,
    private roleService: RoleService,
    private employeeService: EmployeeService
  ) {}

  /**
   * Fetch all locations for the current user with caching
   */
  getLocations(): Observable<any> {
    const now = Date.now();

    // Return cached data if still valid
    if (this.locationsCache$ && now - this.lastFetchTime < this.CACHE_DURATION) {
      return this.locationsCache$;
    }

    // If already loading, return the existing observable to prevent duplicate calls
    if (this.isLoadingLocations && this.locationsCache$) {
      return this.locationsCache$;
    }

    // Mark as loading
    this.isLoadingLocations = true;

    // Create new observable with caching
    this.locationsCache$ = this.apiService.get('location/view-location-user').pipe(
      tap(response => {
        if (response.status && response.data) {
          this.locationsSubject.next(response.data);
          this.ensureDefaultShopLocation(response.data);
          this.lastFetchTime = now;
        }
        this.isLoadingLocations = false;
      }),
      catchError(error => {
        this.logger.error('Error fetching locations:', error);
        // Return empty array on error to prevent breaking the UI
        this.locationsSubject.next([]);
        this.isLoadingLocations = false;
        return of({ status: false, data: [] });
      }),
      shareReplay(1) // Cache the result for multiple subscribers
    );

    return this.locationsCache$;
  }

  /**
   * Load locations and update the subject
   * Only makes API call if locations are not already loaded
   */
  loadLocations(): void {
    // If locations are already loaded, don't make another API call
    if (this.hasLocations()) {
      return;
    }

    this.getLocations().subscribe({
      next: response => {
        if (response.status && response.data) {
          this.locationsSubject.next(response.data);
        } else {
          this.locationsSubject.next([]);
        }
      },
      error: error => {
        this.logger.error('Failed to load locations:', error);
        this.locationsSubject.next([]);
      },
    });
  }

  /**
   * Clear location cache - useful when user changes
   */
  clearLocationCache(): void {
    this.shopLocationWriteGeneration = this.shopLocationWriteGeneration + 1;
    this.locationsCache$ = undefined;
    this.lastFetchTime = 0;
    this.isLoadingLocations = false;
    this.locationsSubject.next([]);
    clearShopLocationState();
  }

  /**
   * Drop cached fetch state so the next `getLocations()` hits the API, but keep the
   * current in-memory list (avoids an empty-state flash after location edit while refresh runs).
   * For logout or full reset, use {@link clearLocationCache}.
   */
  invalidateLocationsFetchCache(): void {
    this.locationsCache$ = undefined;
    this.lastFetchTime = 0;
    this.isLoadingLocations = false;
  }

  /**
   * Get location options for dropdown
   */
  getLocationOptions(): LocationOption[] {
    const locations = this.locationsSubject.value;
    return locations
      .filter(location => location.status) // Only show active locations
      .map(location => {
        // Show only branch name
        return {
          label: location.branch,
          value: location.id,
        };
      });
  }

  /**
   * Get location by ID
   */
  getLocationById(locationId: string): LocationApiResponse | undefined {
    return this.locationsSubject.value.find(location => location.id === locationId);
  }

  /**
   * Get current locations value
   */
  getCurrentLocations(): LocationApiResponse[] {
    return this.locationsSubject.value;
  }

  /**
   * Clear cache and force refresh
   */
  refreshLocations(): void {
    this.locationsCache$ = undefined;
    this.lastFetchTime = 0;
    // Force refresh by calling getLocations directly (bypasses loadLocations early return)
    this.getLocations().subscribe({
      next: response => {
        if (response.status && response.data) {
          this.locationsSubject.next(response.data);
        } else {
          this.locationsSubject.next([]);
        }
      },
      error: error => {
        this.logger.error('Failed to refresh locations:', error);
        this.locationsSubject.next([]);
      },
    });
  }

  /**
   * Check if locations are loaded
   */
  hasLocations(): boolean {
    return this.locationsSubject.value.length > 0;
  }

  /**
   * Returns an Observable of location ID strings for the current user.
   * Attendant/staff: single assigned employee location. Admin: all location IDs.
   * Shared by header and notification-dropdown components.
   */
  getLocationIdsForCurrentUser(): Observable<string[]> {
    const userRole = this.roleService.getCurrentUserRole();

    if (
      userRole === UserRole.ATTENDANT ||
      userRole === UserRole.STAFF ||
      userRole === UserRole.ATTENDANT_PLUS
    ) {
      return this.employeeService.getCurrentEmployeeProfile().pipe(
        take(1),
        map((response: any) => {
          if (response.status && response.data?.location_id) {
            return [response.data.location_id];
          }
          return [];
        }),
        catchError(() => of([]))
      );
    }
    return this.getLocations().pipe(
      take(1),
      map((response: any): string[] => {
        if (response.status && response.data && Array.isArray(response.data)) {
          const ids: string[] = response.data
            .map((loc: { id?: string }) => loc.id)
            .filter(
              (id: string | undefined): id is string => typeof id === 'string' && id.length > 0
            );
          return [...new Set(ids)];
        }
        return [];
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Update locations from external data (e.g., from ProfileService)
   * This syncs LocationService with ProfileService without making an API call
   */
  updateLocationsFromData(locations: LocationApiResponse[]): void {
    this.locationsSubject.next(locations);
    this.ensureDefaultShopLocation(locations);
    // Clear cache to ensure next getLocations() call fetches fresh data
    this.locationsCache$ = undefined;
    this.lastFetchTime = 0;
  }

  getDefaultShopLocation(): LocationApiResponse | null {
    return readShopLocationState()?.default ?? null;
  }

  getSelectedShopLocation(): LocationApiResponse | null {
    return readShopLocationState()?.selected ?? null;
  }

  getDefaultShopTimezone(): string {
    return readTimezoneFromLocation(this.getDefaultShopLocation()) ?? '';
  }

  /** Selected shop timezone when set, otherwise default shop from initial load. */
  getEffectiveShopTimezone(): string {
    const state = readShopLocationState();
    return (
      readTimezoneFromLocation(state?.selected) ?? readTimezoneFromLocation(state?.default) ?? ''
    );
  }

  /**
   * Timezone for a filtered location; when no id, uses selected ?? default.
   */
  getTimezoneForLocationId(locationId?: string | null): string {
    if (locationId) {
      const fromCache = this.getLocationById(locationId);
      const timezone = readTimezoneFromLocation(fromCache);
      if (timezone) {
        return timezone;
      }
    }
    return this.getEffectiveShopTimezone();
  }

  /** Update `shop_location_state.selected` when the user picks a location filter. */
  setShopLocationById(locationId: string): void {
    if (!locationId || typeof window === 'undefined') {
      return;
    }
    const location = this.getLocationById(locationId);
    if (!location) {
      return;
    }
    const state = readShopLocationState() ?? { default: null, selected: null };
    writeShopLocationState({ ...state, selected: location });
  }

  /** Clear only `selected`; keeps `default` from initial locations load. */
  clearShopLocation(): void {
    const state = readShopLocationState();
    if (!state) {
      return;
    }
    writeShopLocationState({ ...state, selected: null });
  }

  /** Effective shop for display: selected ?? default. */
  getShopLocation(): LocationApiResponse | null {
    const state = readShopLocationState();
    return state?.selected ?? state?.default ?? null;
  }

  private ensureDefaultShopLocation(locations: LocationApiResponse[]): void {
    if (this.roleService.isShopEmployee()) {
      this.shopLocationWriteGeneration = this.shopLocationWriteGeneration + 1;
      const generation = this.shopLocationWriteGeneration;
      this.employeeService
        .getCurrentEmployeeProfile()
        .pipe(take(1))
        .subscribe({
          next: response => {
            if (generation !== this.shopLocationWriteGeneration) {
              return;
            }
            if (!this.roleService.isShopEmployee()) {
              return;
            }
            const locationId =
              response.status && response.data?.location_id
                ? String(response.data.location_id)
                : getUserDataLocationId();
            this.writeDefaultShopLocation(locations, locationId);
          },
          error: () => {
            if (generation !== this.shopLocationWriteGeneration) {
              return;
            }
            if (!this.roleService.isShopEmployee()) {
              return;
            }
            this.writeDefaultShopLocation(locations, getUserDataLocationId());
          },
        });
      return;
    }

    this.writeDefaultShopLocation(locations);
  }

  private writeDefaultShopLocation(
    locations: LocationApiResponse[],
    preferredLocationId?: string
  ): void {
    const preferred = preferredLocationId
      ? locations.find(location => location.id === preferredLocationId && location.status === true)
      : undefined;
    const defaultLocation = preferred ?? locations.find(location => location.status === true);
    if (!defaultLocation) {
      return;
    }

    const isShopFloorEmployee = this.roleService.isShopEmployee();
    const state = readShopLocationState();
    const selected = isShopFloorEmployee
      ? null
      : state?.selected
        ? (locations.find(location => location.id === state.selected?.id) ?? null)
        : null;

    writeShopLocationState({
      default: defaultLocation,
      selected,
    });
  }
}
